<template lang="pug">
#header
	.route-hierarchy(v-if="routeHierarchy.length")
		template(v-for="(routeItem, index) in routeHierarchy")
			router-link(:to="routeItem.route") {{ routeItem.displayName }}
			VIcon(
				name="arrow/right.simple",
				v-if="index < routeHierarchy.length - 1"
			)
	.left
		h1
			slot
		.labels(v-if="labels && labels.length")
			KPill(
				v-for="(label, index) in labels",
				:key="index",
				variant="main",
				big
			) {{ label }}
	VIcon.header-icon(v-if="icon", :name="icon")
	slot(name="actions")

.header-details(v-if="lifecycle || showIdentifier")
	.lifecycle(
		v-if="lifecycle",
		:class="{ editable: canEditLifecycle }",
		@click="triggerLifecycleModal"
	)
		VIcon(v-if="iconName", :name="iconName")
		span {{ $lifecycle.name(original ?? lifecycle) }}
		span(v-if="original") &nbsp; ({{ lifecycle }})
	IdentifierCopy(v-if="showIdentifier") {{ currentRouteId }}
	TheLifecycleModal(
		v-if="showLifecycleModal",
		@closeModal="showLifecycleModal = false",
		@updateLifecycle="(lifecycle) => $emit('updateLifecycle', lifecycle)",
		:initialState="lifecycle",
		:resource
	)
</template>

<script>
import { mapGetters, mapState } from "vuex";

import IdentifierCopy from "@/components/base/IdentifierCopy.vue";
import TheLifecycleModal from "@/components/single/TheLifecycleModal.vue";

export default {
	name: "VTheHeader",
	components: {
		IdentifierCopy,
		TheLifecycleModal
	},
	props: {
		labels: {
			type: Array,
			required: false,
			default: () => []
		},
		icon: {
			type: String,
			required: false,
			default: ""
		},
		currentRouteTitle: {
			type: String,
			required: false,
			default: ""
		},
		showIdentifier: {
			type: Boolean,
			required: false,
			default: false
		},
		contactModuleName: {
			type: String,
			required: false,
			default: null
		},
		lifecycle: {
			type: String,
			required: false,
			default: ""
		},
		canEditLifecycle: {
			type: Boolean,
			required: false,
			default: true
		},
		original: {
			type: String,
			required: false,
			default: null
		},
		resource: {
			type: Object,
			required: false,
			default: null
		}
	},
	emits: ["updateLifecycle"],
	data() {
		return {
			routeHierarchy: [],
			showLifecycleModal: false
		};
	},
	computed: {
		interactableLifecycle() {
			return !!this.lifecycle && this.canEditLifecycle;
		},
		iconName() {
			return this.$lifecycle.icon(this.lifecycle);
		},
		currentRouteId() {
			return this.$route.params.id;
		},
		...mapState({
			contact(state, getters) {
				return getters[`${this.contactModuleName}/contact`];
			}
		}),
		...mapGetters({
			findOrganization: "organizations/find",
			findTenant: "tenants/find",
			findMissionProfile: "missionProfiles/find",
			findSpacecraft: "spacecrafts/find",
			findStation: "stations/find",
			findSystem: "systems/find",
			findContact: "contacts/find"
		})
	},
	created() {
		this.createRouteHierarchy();
	},
	methods: {
		triggerLifecycleModal() {
			if (this.interactableLifecycle) {
				this.showLifecycleModal = true;
			}
		},
		createRouteHierarchy() {
			this.routeHierarchy = [];
			this.addToRouteHierachy(this.$route, this.currentRouteTitle);
		},
		addToRouteHierachy(route, displayName = null) {
			const props = this.getRouteProperties(route, displayName);

			if (route.meta.parentName) {
				let params = {};
				if (props.parentId) {
					params.id = props.parentId;
				}

				const parentRoute = this.$router.resolve({
					name: route.meta.parentName,
					params
				});
				this.addToRouteHierachy(parentRoute);
			}

			this.routeHierarchy.push({ route, displayName: props.displayName });
		},
		getRouteProperties(route, displayName) {
			if (route.params.id) {
				const subPath = route.fullPath.split("/")[1];
				switch (subPath) {
					case "mission_profiles": {
						const missionProfile = this.findMissionProfile(route.params.id);
						const parentId = this.isSubRoute(route)
							? missionProfile.id
							: missionProfile.tenant;
						return {
							displayName: this.getDisplayName(
								route,
								displayName,
								missionProfile
							),
							parentId
						};
					}
					case "tenants": {
						const tenant = this.findTenant(route.params.id);
						const parentId = this.isSubRoute(route)
							? tenant.id
							: tenant.organizationId;
						return {
							displayName: this.getDisplayName(route, displayName, tenant),
							parentId
						};
					}
					case "organizations": {
						const organization = this.findOrganization(route.params.id);
						const parentId = this.isSubRoute(route) ? organization.id : null;
						return {
							displayName: this.getDisplayName(
								route,
								displayName,
								organization
							),
							parentId
						};
					}
					case "spacecrafts": {
						const spacecraft = this.findSpacecraft(route.params.id);
						const parentId = this.isSubRoute(route) ? spacecraft.id : null;
						return {
							displayName: this.getDisplayName(route, displayName, spacecraft),
							parentId
						};
					}
					case "stations": {
						const station = this.findStation(route.params.id);
						const parentId = this.isEditRoute(route) ? station.id : null;
						return {
							displayName: this.getDisplayName(route, displayName, station),
							parentId
						};
					}
					case "systems": {
						const system = this.findSystem(route.params.id);
						const parentId = this.isSubRoute(route) ? system.id : null;
						return {
							displayName: this.getDisplayName(route, displayName, system),
							parentId
						};
					}
					case "contacts": {
						displayName = this.getDisplayName(route, displayName);
						const parentId = this.isEditRoute(route) ? this.contact.id : null;

						if (this.contactModuleName && !this.isEditRoute(route)) {
							displayName =
								this.contact.spacecraft.name +
								" on " +
								this.contact.system.name;
						}
						return { displayName, parentId };
					}
				}
			}

			return {
				displayName: this.getDisplayName(route, displayName, null),
				parentId: null
			};
		},
		isEditRoute(route) {
			return route.fullPath.split("/").includes("edit");
		},
		isNewRoute(route) {
			return route.fullPath
				.split("/")
				.some((subPath) => subPath.startsWith("new"));
		},
		isInheritRoute(route) {
			return route.fullPath.split("/").includes("inherit_associations");
		},
		isSubRoute(route) {
			return (
				this.isEditRoute(route) ||
				this.isNewRoute(route) ||
				this.isInheritRoute(route)
			);
		},
		getDisplayName(route, displayName, asset) {
			if (displayName) {
				return displayName;
			}
			if (this.isEditRoute(route)) {
				return "Edit";
			}

			return asset?.name ?? route.meta?.title ?? route.name;
		}
	}
};
</script>

<style lang="scss" scoped>
#header,
.header-details {
	--header-section-spacing: var(--standard-padding-height);
	--header-title-gap: calc(var(--standard-gap-width) - #{rem-calc(2)});
	--header-mobile-stack-gap: calc(var(--standard-margin-height) * 2);
}

#header {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	border-bottom: 1px solid hsl(var(--white20));
	padding-bottom: var(--header-section-spacing);
	margin-bottom: var(--header-section-spacing);

	@media (max-width: $mobile-breakpoint) {
		flex-flow: column;

		.type {
			margin: 0 0 var(--header-mobile-stack-gap);
		}
	}
}

.route-hierarchy {
	width: 100%;
	color: hsl(var(--white40));
	margin-bottom: calc(var(--standard-gap-width) - #{rem-calc(7)});
	display: flex;
	align-items: center;
	flex-grow: 1;
	order: 2;

	@media (min-width: $mobile-breakpoint) {
		order: unset;
	}

	.icon #{v-deep('.stroke-primary')} {
		stroke: hsl(var(--white40));
	}

	a {
		display: inline-block;
		color: inherit;
		text-decoration: none;
	}
}

.left {
	flex-grow: 1;
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	min-width: 0;
	order: 3;

	@media (max-width: $mobile-breakpoint) {
		width: 100%;
	}

	@media (min-width: $mobile-breakpoint) {
		order: unset;
	}
}

h1 {
	display: block;
	margin: 0 var(--header-title-gap) 0 0;
	line-height: 1.2;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.labels {
	.kcl-pill {
		&:not(:last-child) {
			margin-right: calc(var(--standard-gap-width) - #{rem-calc(7)});
		}
	}
}

.header-details {
	display: flex;
	justify-content: space-between;
	margin-bottom: var(--header-section-spacing);

	@media (max-width: $mobile-breakpoint) {
		flex-flow: column;
	}

	.identifier {
		margin-left: auto;
	}
}

.header-icon {
	margin-bottom: var(--header-mobile-stack-gap);
	order: 1;

	@media (min-width: $mobile-breakpoint) {
		order: unset;
		margin: 0;
	}
}

.lifecycle {
	display: flex;
	align-items: center;
	padding: calc(var(--standard-padding-height) - #{rem-calc(3)})
		calc(var(--standard-padding-width) + #{rem-calc(1)});
	border-radius: var(--global-radius);
	background-color: hsl(var(--white10));
	border: 1px solid transparent;
	user-select: none;

	&.editable {
		cursor: pointer;

		&:hover {
			border-color: hsl(var(--white40));
		}
	}

	.icon {
		margin-right: calc(var(--standard-gap-width) - #{rem-calc(4)});
	}

	& + .identifier {
		margin-left: var(--standard-gap-width);

		@media (max-width: $mobile-breakpoint) {
			margin: var(--header-title-gap) 0 0;
		}
	}
}
</style>
